import React, { useCallback, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { reloadAllJobInterviewAssessments } from "../../../services/job.service";
import { useAllJobOffers, useDeleteJobOffer } from "../../../hooks/useJobOffers";
import { toast } from "sonner";
import i18n from "@/i18n";
import { Briefcase, FolderOpen, Search } from "lucide-react";
import { DataTableOrganism } from "@/components/ui/organisms";
import {
  AccentBarHeading,
  IllustratedEmptyState,
  SummaryStatCard,
} from "@/components/ui/molecules";
import { SummarizedListSectionTemplate } from "@/components/ui/templates";
import {
  createDeletableJobsTableColumns,
  mapJobOfferToDeletableRow,
} from "./deletableJobsTable.columns";

/** Backend caps page size at 100; one page + DataTable client search matches other admin tabs. */
const ADMIN_JOBS_FETCH_SIZE = 100;

interface DeletableJobsListProps {
  isAdmin: boolean;
  isLoadingAuth: boolean;
}

export const DeletableJobsList: React.FC<DeletableJobsListProps> = ({
  isAdmin,
  isLoadingAuth,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const deleteJobMutation = useDeleteJobOffer();

  const {
    data,
    isLoading: isLoadingJobs,
    error: loadError,
  } = useAllJobOffers(
    undefined,
    1,
    ADMIN_JOBS_FETCH_SIZE,
    undefined,
    "",
    { enabled: !isLoadingAuth && isAdmin },
    "admin"
  );

  const jobs = data?.items ?? [];
  const totalJobs = data?.total ?? 0;
  const error = loadError?.message ?? null;

  const handleDeleteJob = useCallback(
    async (jobId: string, jobTitle: string) => {
      if (
        !window.confirm(
          `Are you sure you want to delete "${jobTitle}"? This action cannot be undone.`
        )
      ) {
        return;
      }

      try {
        await deleteJobMutation.mutateAsync(jobId);
        toast.success(i18n.t('common:toasts.admin.job_deleted', { title: jobTitle }));
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : i18n.t('common:toasts.unexpected_error');
        toast.error(i18n.t('common:toasts.admin.job_delete_failed', { error: errorMessage }));
      }
    },
    [deleteJobMutation]
  );

  const handleReloadInterviews = useCallback(
    async (jobId: string, jobTitle: string) => {
      if (
        !window.confirm(
          `Are you sure you want to reload all interview assessments for "${jobTitle}"? This will regenerate all assessments for completed interviews.`
        )
      ) {
        return;
      }
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await reloadAllJobInterviewAssessments(accessToken, jobId);
        if (response.error) {
          toast.error(i18n.t('common:toasts.admin.interviews_reload_failed', { error: response.error }));
        } else if (response.data) {
          const { reloaded_count, total_completed_interviews, failed_interviews } = response.data;
          if (reloaded_count > 0) {
            toast.success(
              i18n.t('common:toasts.admin.interviews_reload_success', {
                reloaded: reloaded_count,
                total: total_completed_interviews,
              })
            );
            if (failed_interviews && failed_interviews.length > 0) {
              toast.warning(i18n.t('common:toasts.admin.interviews_reload_partial', { count: failed_interviews.length }));
            }
          } else {
            toast.info(response.data.message);
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : i18n.t('common:toasts.unexpected_error');
        toast.error(i18n.t('common:toasts.admin.interviews_reload_failed', { error: errorMessage }));
      }
    },
    [getAccessTokenSilently]
  );

  const tableRows = useMemo(() => jobs.map(mapJobOfferToDeletableRow), [jobs]);

  const jobColumns = useMemo(
    () =>
      createDeletableJobsTableColumns({
        onReload: handleReloadInterviews,
        onDelete: handleDeleteJob,
      }),
    [handleReloadInterviews, handleDeleteJob]
  );

  const emptyState = useMemo(() => {
    if (tableRows.length === 0) {
      return (
        <IllustratedEmptyState
          icon={<FolderOpen />}
          title="No jobs available"
          description="No jobs found in the system."
        />
      );
    }
    return (
      <IllustratedEmptyState
        icon={<Search />}
        title="No matching jobs"
        description="Try adjusting your search by company, email, title, or status."
      />
    );
  }, [tableRows.length]);

  if (!isAdmin) {
    return null;
  }

  if (isLoadingAuth || isLoadingJobs) {
    return (
      <SummarizedListSectionTemplate status="loading" loadingMessage="Loading jobs…" />
    );
  }

  if (error) {
    return (
      <SummarizedListSectionTemplate
        status="error"
        errorTitle="Error loading jobs"
        errorMessage={error}
      />
    );
  }

  return (
    <SummarizedListSectionTemplate
      status="ready"
      sectionGap="relaxed"
      summary={
        <SummaryStatCard
          label="Total jobs"
          value={totalJobs}
          icon={<Briefcase />}
        />
      }
      primary={
        <DataTableOrganism
          columns={jobColumns}
          data={tableRows}
          pageSize={10}
          enableSelection
          enableSearch
          enableColumnVisibility
          columnVisibilityLabel="Column visibility"
          toolbarTitle={<AccentBarHeading>Job management</AccentBarHeading>}
          searchPlaceholder="Search by company, email, title, status…"
          searchKeys={["companyName", "creatorEmail", "jobTitle", "statusDisplay"]}
          paginationItemLabel="jobs"
          dataTableProps={{
            emptyState,
          }}
        />
      }
    />
  );
};
